const degreesToRadians = (degrees: number) => (degrees * Math.PI) / 180;

export enum MapTileSizeMethod {
  none = 'none',
  ceil = 'ceil',
}

// Implement common calculations for map tiling with a Mercator projection.
//
// There are three coordinate systems we are managing.
//   1.  The latitude/longitude geographic coordinates.  Mercator displays from
//       from -180° to 180° longitude, and -85.0511° to 85.0511° latitude.
//   2.  The map tile coordinates (tileX, tileY).  This uses 512x512 tiles, with x=0 being
//       the leftmost image pixel at -180° longitude, y=0 being the topmost image
//       pixel at 85.0511° latitude, both of which lie in tile (0, 0).
//   3.  The view coordinates (viewX, viewY).  This is a scaled window onto tile
//       coordinates based on the center, zoom, width, and height properties provided.
type useMercatorMapProps = {
  center: number[];
  zoom: number;
  viewWidth: number;
  viewHeight: number;
  tileDimension: number;
  tileSizeMethod: MapTileSizeMethod;
};
export const useMercatorMap = ({
  center,
  zoom,
  viewWidth,
  viewHeight,
  tileDimension,
  tileSizeMethod,
}: useMercatorMapProps) => {
  const minLongitude = -180;
  const maxLongitude = 180;
  const longitudeRange = maxLongitude - minLongitude;

  // The zoom level we will use when requesting tiles.
  const tileZoom = Math.round(zoom);

  // Return the total dimension of the tile zoom being used.  Mercator is a
  // square projection, so the dimension is equal in the X and Y directions.
  const totalTileDimension = Math.pow(2, tileZoom) * tileDimension;

  // Return the total dimension of the tiles scaled to the view area.  This
  // accounts for non-integer zoom values.
  const totalViewDimension = Math.pow(2, zoom) * tileDimension;

  // The scale from view coordinates to tile coordinates.
  const viewTileRatio = totalViewDimension / totalTileDimension;

  // Convert an X in tile coordinates to a longitude.
  const tileXToLongitude = (x: number) => {
    const xRatio = x / totalTileDimension;

    return minLongitude + longitudeRange * xRatio;
  };

  // Convert a Y in tile coordinates to a latitude.
  const tileYToLatitude = (y: number) => {
    const yRatio = 0.5 - (totalTileDimension - y) / totalTileDimension;

    return -90 + (360 * Math.atan(Math.exp(-yRatio * 2 * Math.PI))) / Math.PI;
  };

  // Convert a longitude to an X in tile coordinates.
  const longitudeToTileX = (longitude: number) => {
    const longitudeRatio = (longitude - minLongitude) / longitudeRange;

    return totalTileDimension * longitudeRatio;
  };

  // Convert a latitude to a Y in tile coordinates.
  const latitudeToTileY = (latitude: number) => {
    const sinLatitude = Math.sin(degreesToRadians(latitude));
    const latitudeRatio = 0.5 - Math.log((1 + sinLatitude) / (1 - sinLatitude)) / (4 * Math.PI);

    return totalTileDimension * latitudeRatio;
  };

  // The visible rect in view coordinates.
  const viewVisibleRect = {
    x: 0,
    y: 0,
    width: viewWidth,
    height: viewHeight,
  };

  // The visible rect in tile coordinates.
  const tileVisibleRect = {
    x: longitudeToTileX(center ? center[0] : 0) - viewVisibleRect.width / viewTileRatio / 2,
    y: latitudeToTileY(center ? center[1] : 0) - viewVisibleRect.height / viewTileRatio / 2,
    width: viewVisibleRect.width / viewTileRatio,
    height: viewVisibleRect.height / viewTileRatio,
  };

  // Convert an X in tile coordinates to an X in view coordinates.
  const tileToViewX = (x: number) =>
    viewVisibleRect.x + viewVisibleRect.width * ((x - tileVisibleRect.x) / tileVisibleRect.width);

  // Convert a Y in tile coordinates to a Y in view coordinates.
  const tileToViewY = (y: number) =>
    viewVisibleRect.y + viewVisibleRect.height * ((y - tileVisibleRect.y) / tileVisibleRect.height);

  // Convert an X in view coordinates to an X in tile coordinates.
  const viewToTileX = (x: number) =>
    tileVisibleRect.x + tileVisibleRect.width * ((x - viewVisibleRect.x) / viewVisibleRect.width);

  // Convert a Y in view coordinates to a Y in tile coordinates.
  const viewToTileY = (y: number) =>
    tileVisibleRect.y + tileVisibleRect.height * ((y - viewVisibleRect.y) / viewVisibleRect.height);

  // Convert an (x, y) point in view coordinates to (longitude, latitude).
  const viewPointToCoords = (x: number, y: number) =>
    // Step through tile coordinates along the way.
    [tileXToLongitude(viewToTileX(x)), tileYToLatitude(viewToTileY(y))];
  // Convert a (longitude, latitude) to an (x, y) in view coordinates.
  const coordsToViewPoint = (longitude: number, latitude: number) =>
    // Step through tile coordinates along the way.
    [Math.round(tileToViewX(longitudeToTileX(longitude))), Math.round(tileToViewY(latitudeToTileY(latitude)))];
  // Return the tile {Y} integer value.  Tiles start at row 0 in the north.
  const tileRowForLatitude = (latitude: number) => {
    const tileY = latitudeToTileY(latitude);

    return Math.floor(tileY / tileDimension);
  };

  // Return the tile {X} integer value.  Tiles start at column 0 in the west.
  const tileColumnForLongitude = (longitude: number) => {
    const tileX = longitudeToTileX(longitude);

    return Math.floor(tileX / tileDimension);
  };

  // Return view geometry for the specified tile.
  const viewRectForTile = (column: number, row: number) => {
    if (tileSizeMethod === MapTileSizeMethod.none) {
      const tileSize = tileDimension * viewTileRatio;

      return {
        x: tileToViewX(column * tileDimension),
        y: tileToViewY(row * tileDimension),
        width: tileSize,
        height: tileSize,
      };
    }
    const tileSize = Math.ceil(tileDimension * viewTileRatio);

    return {
      x: Math.round(tileToViewX(column * tileDimension)),
      y: Math.round(tileToViewY(row * tileDimension)),
      width: tileSize,
      height: tileSize,
    };
  };

  // Return geographic geometry for the specified tile.
  const geoRectForTile = (column: number, row: number) => ({
    minLatitude: tileYToLatitude((row + 1) * tileDimension),
    maxLatitude: tileYToLatitude(row * tileDimension),
    minLongitude: tileXToLongitude(column * tileDimension),
    maxLongitude: tileXToLongitude((column + 1) * tileDimension),
  });

  return {
    tileDimension,
    longitudeToTileX,
    latitudeToTileY,
    viewPointToCoords,
    coordsToViewPoint,
    tileRowForLatitude,
    tileColumnForLongitude,
    viewRectForTile,
    geoRectForTile,
    tileZoom,
  };
};
